import axios from 'axios';
import { isTransientFailure, nextRetryDelay, temporaryUnavailableMessage } from './transientRetry';
import { translateApiError } from './apiErrorText';

const getWindowOrigin = () => (
  typeof window !== 'undefined' && typeof window.location?.origin === 'string'
    ? window.location.origin
    : ''
);

// Fix Mixed Content: ensure HTTPS in production
let API_BASE_URL = process.env.REACT_APP_BACKEND_URL || getWindowOrigin();

// Force HTTPS if page is loaded over HTTPS
if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
  API_BASE_URL = API_BASE_URL.replace(/^http:\/\//, 'https://');
}

// Export the safe API URL for use with fetch() in other components
export const getApiUrl = () => {
  let url = process.env.REACT_APP_BACKEND_URL || getWindowOrigin();
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    url = url.replace(/^http:\/\//, 'https://');
  }
  return url;
};

// Export API_BASE_URL for backward compatibility
export const API_URL = API_BASE_URL;

// FastAPI returns validation failures as an array of structured objects.  Most
// UI call sites pass `response.data.detail` directly to Sonner/React, where an
// object or array is not a valid child and can blank the whole page.  Keep the
// response contract safe at the shared client boundary.
export const getErrorMessage = (error, defaultMsg = 'An error occurred') => {
  const detail = error?.response?.data?.detail;

  // The backend answers in Russian; show it in the interface language (I18N-08)
  if (typeof detail === 'string' && detail) return translateApiError(detail);
  if (Array.isArray(detail)) {
    const messages = detail
      .map((entry) => {
        if (typeof entry === 'string') return entry;
        if (entry && typeof entry === 'object') {
          return entry.msg || entry.message || '';
        }
        return '';
      })
      .filter(Boolean);
    if (messages.length) return messages.map(translateApiError).join(', ');
  }
  if (detail && typeof detail === 'object') {
    return translateApiError(detail.msg || detail.message) || defaultMsg;
  }
  if (typeof error?.message === 'string' && error.message) {
    return translateApiError(error.message);
  }
  return defaultMsg;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  maxRedirects: 5,  // Allow redirects
});

// Add auth token to requests and ensure HTTPS
api.interceptors.request.use((config) => {
  // Double-check HTTPS for any dynamic URLs
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    if (config.baseURL && config.baseURL.startsWith('http://')) {
      config.baseURL = config.baseURL.replace(/^http:/, 'https:');
    }
    if (config.url && config.url.startsWith('http://')) {
      config.url = config.url.replace(/^http:/, 'https:');
    }
  }
  
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Загрузка файлов: инстанс объявляет Content-Type: application/json, а
  // axios 1.x при JSON-заголовке сериализует FormData в строку — файл
  // превращался в {} и сервер отвечал «нет поля file». Снимаем заголовок,
  // тогда браузер сам проставит multipart с корректным boundary.
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
    delete config.headers['content-type'];
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => {
    // Let screens that summarise the client's data (quick-start progress)
    // refresh after any successful change, without every page wiring it up.
    const method = String(response?.config?.method || 'get').toLowerCase();
    if (['post', 'put', 'patch', 'delete'].includes(method) && typeof window !== 'undefined'
        && !String(response?.config?.url || '').includes('/api/wizard-settings')) {
      try { window.dispatchEvent(new Event('tf:data-changed')); } catch { /* old browsers */ }
    }
    return response;
  },
  (error) => {
    // A deploy restarts the backend (nginx 502 for a few seconds): retry reads.
    const retryDelay = nextRetryDelay(error.config, error);
    if (retryDelay !== null) {
      error.config.__retryCount = (error.config.__retryCount || 0) + 1;
      return new Promise((resolve) => setTimeout(resolve, retryDelay)).then(() => api(error.config));
    }
    // Retries exhausted, or a write: say what happened instead of a bare
    // «Ошибка» or nginx's HTML error page.
    if (isTransientFailure(error)) {
      const message = temporaryUnavailableMessage();
      if (error.response) {
        const data = error.response.data;
        error.response.data = { ...(data && typeof data === 'object' ? data : {}), detail: message };
      } else {
        error.message = message;
      }
    }

    // Many pages read `detail` straight off the response, so translate it here as well:
    // one place covers every consumer (I18N-08).
    if (error.response?.data && typeof error.response.data.detail === 'string') {
      error.response.data.detail = translateApiError(error.response.data.detail);
    }
    const originalDetail = error.response?.data?.detail;
    const normalizedMessage = getErrorMessage(error, '');

    if (
      error.response?.data &&
      originalDetail !== undefined &&
      typeof originalDetail !== 'string'
    ) {
      error.response.data.detail = normalizedMessage || 'An error occurred';
    }

    if (error.response?.status === 401) {
      // Only clear session if it's an authentication error, not authorization
      const errorMessage = normalizedMessage;
      
      // Clear session only for actual auth failures, not permissions issues
      if (errorMessage.includes('Invalid authentication credentials') || 
          errorMessage.includes('Could not validate credentials') ||
          errorMessage.includes('Token has expired')) {
        
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
      // For other 401s (permissions, specific endpoint issues), don't clear session
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authAPI = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
  getMe: () => api.get('/api/auth/me'),
};

// Campaigns API
export const campaignsAPI = {
  list: (params = {}) => api.get('/api/campaigns/', { params }),
  get: (id) => api.get(`/api/campaigns/${id}`),
  create: (data) => api.post('/api/campaigns/', data),
  update: (id, data) => api.put(`/api/campaigns/${id}`, data),
  delete: (id) => api.delete(`/api/campaigns/${id}`),
  getStats: () => api.get('/api/campaigns/stats'),
  // Bulk operations
  bulkUpdateStatus: (campaign_ids, status) => api.post('/api/campaigns/bulk/status', { campaign_ids, status }),
  bulkDelete: (campaign_ids) => api.post('/api/campaigns/bulk/delete', { campaign_ids }),
  bulkCopy: (campaign_ids) => api.post('/api/campaigns/bulk/copy', { campaign_ids }),
};

// Prelands API
export const prelandsAPI = {
  list: (params = {}) => api.get('/api/prelands/', { params }),
  getAll: (params = {}) => api.get('/api/prelands/', { params }),
  get: (id) => api.get(`/api/prelands/${id}`),
  create: (data) => api.post('/api/prelands/', data),
  update: (id, data) => api.put(`/api/prelands/${id}`, data),
  delete: (id) => api.delete(`/api/prelands/${id}`),
  preview: (id) => api.get(`/api/prelands/${id}/preview`)
};

// Offers API
export const offersAPI = {
  list: (params = {}) => api.get('/api/offers/', { params }),
  get: (id) => api.get(`/api/offers/${id}`),
  create: (data) => api.post('/api/offers/', data),
  update: (id, data) => api.put(`/api/offers/${id}`, data),
  delete: (id) => api.delete(`/api/offers/${id}`),
};

// Quizzes API
export const quizzesAPI = {
  list: (vertical) => api.get('/api/quizzes/', { params: { vertical } }),
  get: (id) => api.get(`/api/quizzes/${id}`),
  create: (data) => api.post('/api/quizzes/', data),
  update: (id, data) => api.put(`/api/quizzes/${id}`, data),
  delete: (id) => api.delete(`/api/quizzes/${id}`),
  getForSelect: (vertical) => api.get('/api/quizzes/select/list', { params: { vertical } }),
  getPublic: (id) => api.get(`/api/quizzes/public/${id}`),
  submitAnswers: (data) => api.post('/api/quizzes/answers', data),
  getResults: (id) => api.get(`/api/quizzes/${id}/results`),
  getAnswers: (id, params) => api.get(`/api/quizzes/${id}/answers`, { params }),
};

// Leads API
export const leadsAPI = {
  list: (params) => api.get('/api/leads/', { params }),
  get: (id) => api.get(`/api/leads/${id}`),
  updateStatus: (id, status) => api.put(`/api/leads/${id}/status`, null, { params: { status } }),
  delete: (id) => api.delete(`/api/leads/${id}`),
  getQuizResponses: (id) => api.get(`/api/leads/${id}/quiz-responses`),
  linkQuizResponse: (leadId, answerId) => api.post(`/api/leads/${leadId}/link-quiz-response`, null, { params: { answer_id: answerId } }),
  // Bulk operations
  bulkUpdateStatus: (lead_ids, status) => api.post('/api/leads/bulk/status', { lead_ids, status }),
  bulkDelete: (lead_ids) => api.post('/api/leads/bulk/delete', { lead_ids }),
  bulkExport: (lead_ids) => api.post('/api/leads/bulk/export', { lead_ids }),
};

// Analytics API
export const analyticsAPI = {
  overview: (params) => api.get('/api/analytics/overview', { params }),
  byVertical: (params) => api.get('/api/analytics/by-vertical', { params }),
  byCampaign: (params) => api.get('/api/analytics/by-campaign', { params }),
  funnel: (campaign_id, params) => api.get('/api/analytics/funnel', { params: { campaign_id, ...params } }),
  detailedFunnel: (campaign_id, params) => api.get('/api/analytics/detailed-funnel', { params: { campaign_id, ...params } }),
  threads: (params) => api.get('/api/analytics/threads', { params }),
  timeAnalysis: (params) => api.get('/api/analytics/time-analysis', { params }),
  geo: (params) => api.get('/api/analytics/geo', { params }),
  devices: (params) => api.get('/api/analytics/devices', { params }),
  exportCSV: (dataType, params) => api.get('/api/analytics/export/csv', { 
    params: { data_type: dataType, ...params },
    responseType: 'blob'
  }),
};

// Templates API
export const templatesAPI = {
  list: (vertical) => api.get('/api/templates/prelands', { params: { vertical } }),
  get: (id) => api.get(`/api/templates/prelands/${id}`),
};

// Users API
export const usersAPI = {
  list: () => api.get('/api/users/'),
  get: (id) => api.get(`/api/users/${id}`),
  create: (data) => api.post('/api/users/', data),
  update: (id, data) => api.put(`/api/users/${id}`, data),
  delete: (id) => api.delete(`/api/users/${id}`),
};


// AI API - with extended timeout for LLM operations
export const aiAPI = {
  status: () => api.get('/api/ai/status'),
  generatePrelandContent: (data) => api.post('/api/ai/generate/preland-content', data, { timeout: 180000 }), // 3 min timeout
  generateQuizQuestions: (data) => api.post('/api/ai/generate/quiz-questions', data, { timeout: 180000 }),
  generateHeadlines: (data) => api.post('/api/ai/generate/headlines', data, { timeout: 180000 }),
  analyzeCampaign: (data) => api.post('/api/ai/analyze/campaign', data, { timeout: 180000 }),
  // History endpoints
  getHistory: (params) => api.get('/api/ai/history', { params }),
  getHistoryDetail: (historyId) => api.get(`/api/ai/history/${historyId}`),
  deleteHistory: (historyId) => api.delete(`/api/ai/history/${historyId}`),
  clearHistory: (params) => api.delete('/api/ai/history', { params }),
  // P2 Feature endpoints
  generateABVariants: (data) => api.post('/api/ai/generate/ab-variants', data, { timeout: 180000 }),
  analyzeURL: (data) => api.post('/api/ai/analyze/url', data, { timeout: 60000 }),
  generateImage: (data) => api.post('/api/ai/generate/image', data, { timeout: 120000 }), // 2 min for image gen
  generateMultiLang: (data) => api.post('/api/ai/generate/multi-language', data, { timeout: 300000 }), // 5 min for multi-lang
  // Prompt templates
  getPromptTemplates: (params) => api.get('/api/ai/prompts', { params }),
  savePromptTemplate: (data) => api.post('/api/ai/prompts/save', data),
  usePromptTemplate: (templateId, variables) => api.post(`/api/ai/prompts/${templateId}/use`, variables),
  deletePromptTemplate: (templateId) => api.delete(`/api/ai/prompts/${templateId}`),
  // P3 Feature endpoints
  analyzeSEO: (data) => api.post('/api/ai/seo/analyze', data, { timeout: 60000 }),
  checkUniqueness: (data) => api.post('/api/ai/uniqueness/check', data, { timeout: 60000 }),
  publishToThreads: (data) => api.post('/api/ai/threads/publish', data, { timeout: 30000 }),
  trackPerformance: (data) => api.post('/api/ai/analytics/track', data),
  getAnalyticsDashboard: (params) => api.get('/api/ai/analytics/dashboard', { params }),
  aiAssistantChat: (data) => api.post('/api/ai/assistant/chat', data, { timeout: 180000 }),
};


// Smart Links API — canonical /api/smartlinks (legacy /api/links has only
// list/create/get aliases, so update/delete/QR/summary 404/405 there)
export const smartLinksAPI = {
  list: (params) => api.get('/api/smartlinks/', { params }),
  get: (id) => api.get(`/api/smartlinks/${id}`),
  create: (data) => api.post('/api/smartlinks/', data),
  update: (id, data) => api.put(`/api/smartlinks/${id}`, data),
  delete: (id) => api.delete(`/api/smartlinks/${id}`),
  getQR: (id) => api.get(`/api/smartlinks/${id}/qr`),
  summary: () => api.get('/api/smartlinks/stats/summary'),
};

// Profiles API
export const profilesAPI = {
  list: () => api.get('/api/profiles/'),
  get: (id) => api.get(`/api/profiles/${id}`),
  create: (data) => api.post('/api/profiles/', data),
  update: (id, data) => api.put(`/api/profiles/${id}`, data),
  delete: (id) => api.delete(`/api/profiles/${id}`),
  addLink: (id, data) => api.post(`/api/profiles/${id}/links`, data),
  removeLink: (id, index) => api.delete(`/api/profiles/${id}/links/${index}`),
};

// Costs API
export const costsAPI = {
  summary: () => api.get('/api/costs/summary'),
  byCampaign: () => api.get('/api/costs/by-campaign'),
  list: () => api.get('/api/costs/'),
  create: (data) => api.post('/api/costs/', data),
  update: (id, data) => api.put(`/api/costs/${id}`, data),
  delete: (id) => api.delete(`/api/costs/${id}`),
};

// Webhooks API
export const webhooksAPI = {
  list: () => api.get('/api/webhooks/'),
  get: (id) => api.get(`/api/webhooks/${id}`),
  getLogs: (params = {}) => api.get('/api/webhooks/logs/', { params }),
  create: (data) => api.post('/api/webhooks/', data),
  update: (id, data) => api.put(`/api/webhooks/${id}`, data),
  delete: (id) => api.delete(`/api/webhooks/${id}`),
  test: (id) => api.post(`/api/webhooks/${id}/test`),
};

// Quizzes API extended
export const quizzesAPIExtended = {
  list: (vertical) => api.get('/api/quizzes/', { params: { vertical } }),
  get: (id) => api.get(`/api/quizzes/${id}`),
  create: (data) => api.post('/api/quizzes/', data),
  update: (id, data) => api.put(`/api/quizzes/${id}`, data),
  delete: (id) => api.delete(`/api/quizzes/${id}`),
};

// A/B Testing API
export const abTestsAPI = {
  list: (status) => api.get('/api/ab-tests/', { params: { status } }),
  get: (id) => api.get(`/api/ab-tests/${id}`),
  create: (data) => api.post('/api/ab-tests/', data),
  update: (id, data) => api.put(`/api/ab-tests/${id}`, data),
  delete: (id) => api.delete(`/api/ab-tests/${id}`),
  start: (id) => api.post(`/api/ab-tests/${id}/start`),
  pause: (id) => api.post(`/api/ab-tests/${id}/pause`),
  complete: (id) => api.post(`/api/ab-tests/${id}/complete`),
  duplicate: (id) => api.post(`/api/ab-tests/${id}/duplicate`),
  simulate: (id, days) => api.post(`/api/ab-tests/${id}/simulate`, null, { params: { days } }),
  track: (id, variantId, eventType) => api.post(`/api/ab-tests/${id}/track`, null, { 
    params: { variant_id: variantId, event_type: eventType } 
  }),
  stats: (id) => api.get(`/api/ab-tests/${id}/stats`),
};

// Notifications API
export const notificationsAPI = {
  list: (unreadOnly = false, limit = 50) => api.get('/api/notifications/', { 
    params: { unread_only: unreadOnly, limit } 
  }),
  create: (data) => api.post('/api/notifications/', data),
  markRead: (id) => api.post(`/api/notifications/${id}/read`),
  markAllRead: () => api.post('/api/notifications/read-all'),
  delete: (id) => api.delete(`/api/notifications/${id}`),
  clearAll: () => api.delete('/api/notifications/'),
  getPreferences: () => api.get('/api/notifications/preferences'),
  updatePreferences: (data) => api.put('/api/notifications/preferences', data),
  subscribe: (subscription) => api.post('/api/notifications/subscribe', subscription),
  unsubscribe: (endpoint) => api.post('/api/notifications/unsubscribe', null, { params: { endpoint } }),
  sendTest: () => api.post('/api/notifications/test'),
  getStats: () => api.get('/api/notifications/stats'),
  getDiagnostics: () => api.get('/api/notifications/diagnostics'),
};

// Threads API
export const threadsAPI = {
  getAccounts: () => api.get('/api/threads/accounts'),
  addAccount: (data) => api.post('/api/threads/accounts', data),
  deleteAccount: (id) => api.delete(`/api/threads/accounts/${id}`),
  syncAccount: (id) => api.post(`/api/threads/accounts/${id}/sync`),
  getPosts: (accountId) => api.get('/api/threads/posts', { params: { account_id: accountId } }),
  createPost: (data) => api.post('/api/threads/posts', data),
  schedulePost: (data) => api.post('/api/threads/posts', data),  // same endpoint; backend accepts scheduled_at/scheduled_time
  updatePost: (id, data) => api.put(`/api/threads/posts/${id}`, data),
  deletePost: (id) => api.delete(`/api/threads/posts/${id}`),
  publishPost: (id) => api.post(`/api/threads/posts/${id}/publish`),
  getAnalytics: () => api.get('/api/threads/analytics/accounts'),
  getStatus: () => api.get('/api/threads/status'),
  startOAuth: () => api.get('/api/threads/oauth/authorize'),
};

// Hashtags API
export const hashtagsAPI = {
  list: (params) => api.get('/api/hashtags/', { params }),
  get: (id) => api.get(`/api/hashtags/${id}`),
  create: (data) => api.post('/api/hashtags/', data),
  createBulk: (tags, category) => api.post('/api/hashtags/bulk', tags, { params: { category } }),
  update: (id, data) => api.put(`/api/hashtags/${id}`, data),
  delete: (id) => api.delete(`/api/hashtags/${id}`),
  toggleFavorite: (id) => api.post(`/api/hashtags/${id}/favorite`),
  trackUsage: (id, engagement) => api.post(`/api/hashtags/${id}/track-usage`, null, { params: { engagement } }),
  getTop: (limit) => api.get('/api/hashtags/analytics/top', { params: { limit } }),
  getSuggestions: (baseTag) => api.get('/api/hashtags/suggestions', { params: { base_tag: baseTag } }),
  // Sets
  listSets: () => api.get('/api/hashtags/sets/'),
  getSet: (id) => api.get(`/api/hashtags/sets/${id}`),
  createSet: (data) => api.post('/api/hashtags/sets/', data),
  updateSet: (id, data) => api.put(`/api/hashtags/sets/${id}`, data),
  deleteSet: (id) => api.delete(`/api/hashtags/sets/${id}`),
  copySet: (id) => api.post(`/api/hashtags/sets/${id}/copy`),
};

// Post Templates API
export const postTemplatesAPI = {
  list: (params) => api.get('/api/templates/', { params }),
  get: (id) => api.get(`/api/templates/${id}`),
  create: (data) => api.post('/api/templates/', data),
  update: (id, data) => api.put(`/api/templates/${id}`, data),
  delete: (id) => api.delete(`/api/templates/${id}`),
  duplicate: (id) => api.post(`/api/templates/${id}/duplicate`),
  toggleFavorite: (id) => api.post(`/api/templates/${id}/favorite`),
  use: (id, variables) => api.post(`/api/templates/${id}/use`, { variables }),
  moveToFolder: (id, folderId) => api.put(`/api/templates/${id}/move`, null, { params: { folder_id: folderId } }),
  // Folders
  listFolders: () => api.get('/api/templates/folders/'),
  createFolder: (data) => api.post('/api/templates/folders/', data),
  updateFolder: (id, data) => api.put(`/api/templates/folders/${id}`, data),
  deleteFolder: (id) => api.delete(`/api/templates/folders/${id}`),
  // Presets
  listPresets: (category) => api.get('/api/templates/presets/', { params: { category } }),
  copyPreset: (id) => api.post(`/api/templates/presets/${id}/copy`),
};

// Custom Handbooks API
export const handbooksAPI = {
  list: () => api.get('/api/handbooks/'),
  create: (data) => api.post('/api/handbooks/', data),
  get: (id) => api.get(`/api/handbooks/${id}`),
  update: (id, data) => api.put(`/api/handbooks/${id}`, data),
  delete: (id) => api.delete(`/api/handbooks/${id}`),
  duplicate: (id) => api.post(`/api/handbooks/${id}/duplicate`),
  // Variables
  addVariable: (id, data) => api.post(`/api/handbooks/${id}/variables`, data),
  updateVariable: (id, varName, data) => api.put(`/api/handbooks/${id}/variables/${varName}`, data),
  deleteVariable: (id, varName) => api.delete(`/api/handbooks/${id}/variables/${varName}`),
  export: (id) => api.post(`/api/handbooks/${id}/export`),
};

// Variables API (unified system + custom)
export const variablesAPI = {
  getAll: () => api.get('/api/variables/all'),
  getAllFlat: () => api.get('/api/variables/flat'),
};

// Lead Magnets API
export const leadMagnetsAPI = {
  // Dashboard
  getDashboard: () => api.get('/api/lead-magnets/dashboard'),
  
  // Magnets CRUD
  list: (params) => api.get('/api/lead-magnets/magnets', { params }),
  get: (id) => api.get(`/api/lead-magnets/magnets/${id}`),
  create: (data) => api.post('/api/lead-magnets/magnets', data),
  update: (id, data) => api.put(`/api/lead-magnets/magnets/${id}`, data),
  delete: (id) => api.delete(`/api/lead-magnets/magnets/${id}`),
  toggle: (id) => api.post(`/api/lead-magnets/magnets/${id}/toggle`),
  duplicate: (id) => api.post(`/api/lead-magnets/magnets/${id}/duplicate`),
  getEmbed: (id, style) => api.get(`/api/lead-magnets/magnets/${id}/embed`, { params: { style } }),
  
  // Leads
  getLeads: (params) => api.get('/api/lead-magnets/leads', { params }),
  getLead: (id) => api.get(`/api/lead-magnets/leads/${id}`),
  deleteLead: (id) => api.delete(`/api/lead-magnets/leads/${id}`),
  exportLeads: (magnetId, format) => api.post('/api/lead-magnets/leads/export', null, { params: { magnet_id: magnetId, format } }),
  
  // Templates
  getTemplates: (params) => api.get('/api/lead-magnets/templates', { params }),
  useTemplate: (templateId, customizations) => api.post(`/api/lead-magnets/templates/${templateId}/use`, customizations),
  
  // Public endpoints (no auth)
  capture: (data) => api.post('/api/lead-magnets/capture', data),
  trackView: (magnetId) => api.post(`/api/lead-magnets/track-view/${magnetId}`),
};

// System Analyzer (AI QA) API
export const analyzerApi = {
  runNow: () => api.post('/api/analyzer/run'),
  listRuns: (limit = 20) => api.get('/api/analyzer/runs', { params: { limit } }),
  getRun: (runId) => api.get(`/api/analyzer/runs/${runId}`),
  listReports: (limit = 20) => api.get('/api/analyzer/reports', { params: { limit } }),
  getLatestReport: () => api.get('/api/analyzer/reports/latest'),
  getReport: (reportId) => api.get(`/api/analyzer/reports/${reportId}`),
  chooseProvider: (reportId, scope, provider) =>
    api.post(`/api/analyzer/reports/${reportId}/choose`, { scope, provider }),
  listRecommendations: (params) => api.get('/api/analyzer/recommendations', { params }),
  updateRecommendation: (recommendationId, status, note) =>
    api.patch(`/api/analyzer/recommendations/${recommendationId}`, { status, note }),
  getProviderStats: () => api.get('/api/analyzer/stats/providers'),
  listLessons: () => api.get('/api/analyzer/lessons'),
  updateLesson: (lessonId, active) => api.patch(`/api/analyzer/lessons/${lessonId}`, { active }),
};
